import {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";

import {isEqual, uniq} from "@pg-mono/nodash";

import {IRPStore} from "../../app/rp_reducer";
import {aggregationToCityMap} from "../../offer/map/constants/aggregation_to_city_map";
import {IRegionListMap} from "../../region/types/IRegionListMap";
import {RegionType} from "../../region/types/RegionType";
import {useAppDispatch} from "../../store/hooks";
import {searchFormActions, searchFormSlice} from "../slices/search_form_slice";

interface IProps {
    allAvailableRegions: IRegionListMap[];
    regions: {
        districts: IRegionListMap[];
        surroundings: IRegionListMap[];
        main: IRegionListMap | null;
    };
}

export const useSearchInteractiveRegionSelection = (props: IProps) => {
    const interactiveMapRegionSelection = useSelector((state: IRPStore) => state.search[searchFormSlice.name].interactiveMapRegionSelection);
    const interactiveMapOriginRegions = useSelector((state: IRPStore) => state.search[searchFormSlice.name].interactiveMapOriginRegions);
    const initialSelectedRegionIds = useMemo(() => {
        if (interactiveMapRegionSelection) {
            return interactiveMapRegionSelection;
        }
        if (props.regions.main?.id) {
            return [props.regions.main.id];
        }
        return [];
    }, [interactiveMapRegionSelection, props.regions.main?.id]);
    const selectedRegionIdsState = useState<(string | number)[]>(initialSelectedRegionIds);
    const [selectedRegionIds, setSelectedRegionIds] = selectedRegionIdsState;
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(searchFormActions.updateInteractiveMapRegionSelection(selectedRegionIds));
    }, [selectedRegionIds]);

    const checkAllDistrictBeenSelection = (selectedRegionIdsParam: (string | number)[]) => {
        return (
            selectedRegionIdsParam.includes(props.regions.main?.id || -1) ||
            props.regions.districts.filter((item) => selectedRegionIdsParam.includes(item.id)).length === props.regions.districts.length
        );
    };

    const hasAllDistrictBeenSelected = useMemo(() => {
        return checkAllDistrictBeenSelection(selectedRegionIds);
    }, [props.regions.districts, props.regions.main?.id, selectedRegionIds]);

    const handleSelectedRegionsChange = useCallback(
        (triggeredRegionId: number | string) => {
            const handleRegionSelection = (prev: (string | number)[], triggeredRegion: IRegionListMap) => {
                const isRegionSelected = prev.includes(triggeredRegionId);

                // region is a city district
                if (triggeredRegion.type === RegionType.DISTRICT) {
                    const allDistrictsSelected = checkAllDistrictBeenSelection(prev);

                    // if all district selected, remove city select, select rest districts
                    if (allDistrictsSelected) {
                        return uniq([
                            ...prev.filter(
                                (id) =>
                                    // remove all districts and city
                                    props.regions.districts.some((district) => district.id !== id) && props.regions.main?.id !== id
                            ),
                            // add district besides triggeredRegionId
                            ...props.regions.districts.filter((district) => district.id !== triggeredRegionId).map((district) => district.id)
                        ]);
                    } else if (isRegionSelected) {
                        return prev.filter((id) => id !== triggeredRegionId);
                    } else if (checkAllDistrictBeenSelection([...prev, triggeredRegionId])) {
                        // triggered region is the last not selected districts, add whole city
                        return [...prev.filter((id) => !props.regions.districts.some((district) => district.id === id)), props.regions.main?.id || -1];
                    }

                    return [...prev, triggeredRegionId];
                } else {
                    if (isRegionSelected) {
                        return prev.filter((id) => id !== triggeredRegionId);
                    }

                    return [...prev, triggeredRegionId];
                }
            };

            setSelectedRegionIds((prev) => {
                const triggeredRegion = props.allAvailableRegions.find((item) => item.id === triggeredRegionId);

                // region not found, return prev
                if (!triggeredRegion) {
                    return prev;
                }

                const newSelection = handleRegionSelection(prev, triggeredRegion);

                return newSelection;
            });
        },
        [props.allAvailableRegions, props.regions.districts, props.regions.main?.id]
    );

    const syncSelectedRegionsWithRemoteSelection = (remoteSelectedRegions: {type: RegionType; id: number}[]) => {
        if (!props.allAvailableRegions || props.allAvailableRegions.length === 0) {
            return;
        }

        // sync selection between every map with district selections e.g. header and sticky header
        const remoteSelectedRegionsIds = remoteSelectedRegions.map((item) => item.id);

        if (interactiveMapRegionSelection && isEqual(remoteSelectedRegionsIds, interactiveMapOriginRegions)) {
            setSelectedRegionIds(interactiveMapRegionSelection);
            return;
        }
        dispatch(searchFormActions.updateInteractiveMapOriginRegions(remoteSelectedRegionsIds));

        // one city selected - set only that city
        if (remoteSelectedRegions?.length === 1 && remoteSelectedRegions[0].type === RegionType.CITY) {
            setSelectedRegionIds([remoteSelectedRegions[0].id]);
            return;
        }

        // set all available regions
        if (
            remoteSelectedRegions?.length === 1 &&
            remoteSelectedRegions[0].type === RegionType.AGGREGATION &&
            aggregationToCityMap.has(remoteSelectedRegions[0].id)
        ) {
            setSelectedRegionIds(() => props.allAvailableRegions.map((item) => item.id));
            return;
        }

        // otherwise set selected regions if are available and map city to available districts
        setSelectedRegionIds(() => [
            ...(remoteSelectedRegions
                ?.filter((selectedRegion) => {
                    return props.allAvailableRegions.find((availableRegion) => availableRegion.id === selectedRegion.id);
                })
                .map((item) => item.id) || []),
            // also if whole city is selected and available add itd
            ...(props.regions.main?.id &&
            (remoteSelectedRegions.some((region) => region.id === props.regions.main?.id) ||
                props.regions.districts.every((region) => remoteSelectedRegions.some((remoteSelectedRegion) => remoteSelectedRegion.id === region.id)))
                ? [props.regions.main.id]
                : [])
        ]);
    };

    const toggleSelectedDistrict = () => {
        setSelectedRegionIds((prev) => {
            const allDistrictsSelected = checkAllDistrictBeenSelection(prev);

            if (allDistrictsSelected) {
                // remove city
                const regionIdsWithoutMain = prev.filter((id) => id !== props.regions.main?.id);

                return [
                    // remove districts
                    ...regionIdsWithoutMain.filter((id) => props.regions.districts.some((district) => district.id !== id))
                ];
            } else {
                return [
                    // remove districts
                    ...prev.filter((id) => !props.regions.districts.some((district) => district.id === id)),
                    // add city
                    props.regions.main?.id || -1
                ];
            }
        });
    };

    return {
        checkAllDistrictBeenSelection,
        hasAllDistrictBeenSelected,
        selectedRegionIdsState,
        selectedRegionIds,
        handleSelectedRegionsChange,
        toggleSelectedDistrict,
        syncSelectedRegionsWithRemoteSelection
    };
};
import {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";

import {isEqual, uniq} from "@pg-mono/nodash";

import {IRPStore} from "../../app/rp_reducer";
import {aggregationToCityMap} from "../../offer/map/constants/aggregation_to_city_map";
import {IRegionListMap} from "../../region/types/IRegionListMap";
import {RegionType} from "../../region/types/RegionType";
import {useAppDispatch} from "../../store/hooks";
import {searchFormActions, searchFormSlice} from "../slices/search_form_slice";

interface IProps {
    allAvailableRegions: IRegionListMap[];
    regions: {
        districts: IRegionListMap[];
        surroundings: IRegionListMap[];
        main: IRegionListMap | null;
    };
}

export const useSearchInteractiveRegionSelection = (props: IProps) => {
    const interactiveMapRegionSelection = useSelector((state: IRPStore) => state.search[searchFormSlice.name].interactiveMapRegionSelection);
    const interactiveMapOriginRegions = useSelector((state: IRPStore) => state.search[searchFormSlice.name].interactiveMapOriginRegions);
    const initialSelectedRegionIds = useMemo(() => {
        if (interactiveMapRegionSelection) {
            return interactiveMapRegionSelection;
        }
        if (props.regions.main?.id) {
            return [props.regions.main.id];
        }
        return [];
    }, [interactiveMapRegionSelection, props.regions.main?.id]);
    const selectedRegionIdsState = useState<(string | number)[]>(initialSelectedRegionIds);
    const [selectedRegionIds, setSelectedRegionIds] = selectedRegionIdsState;
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(searchFormActions.updateInteractiveMapRegionSelection(selectedRegionIds));
    }, [selectedRegionIds]);

    const checkAllDistrictBeenSelection = (selectedRegionIdsParam: (string | number)[]) => {
        return (
            selectedRegionIdsParam.includes(props.regions.main?.id || -1) ||
            props.regions.districts.filter((item) => selectedRegionIdsParam.includes(item.id)).length === props.regions.districts.length
        );
    };

    const hasAllDistrictBeenSelected = useMemo(() => {
        return checkAllDistrictBeenSelection(selectedRegionIds);
    }, [props.regions.districts, props.regions.main?.id, selectedRegionIds]);

    const handleSelectedRegionsChange = useCallback(
        (triggeredRegionId: number | string) => {
            const handleRegionSelection = (prev: (string | number)[], triggeredRegion: IRegionListMap) => {
                const isRegionSelected = prev.includes(triggeredRegionId);

                // region is a city district
                if (triggeredRegion.type === RegionType.DISTRICT) {
                    const allDistrictsSelected = checkAllDistrictBeenSelection(prev);

                    // if all district selected, remove city select, select rest districts
                    if (allDistrictsSelected) {
                        return uniq([
                            ...prev.filter(
                                (id) =>
                                    // remove all districts and city
                                    props.regions.districts.some((district) => district.id !== id) && props.regions.main?.id !== id
                            ),
                            // add district besides triggeredRegionId
                            ...props.regions.districts.filter((district) => district.id !== triggeredRegionId).map((district) => district.id)
                        ]);
                    } else if (isRegionSelected) {
                        return prev.filter((id) => id !== triggeredRegionId);
                    } else if (checkAllDistrictBeenSelection([...prev, triggeredRegionId])) {
                        // triggered region is the last not selected districts, add whole city
                        return [...prev.filter((id) => !props.regions.districts.some((district) => district.id === id)), props.regions.main?.id || -1];
                    }

                    return [...prev, triggeredRegionId];
                } else {
                    if (isRegionSelected) {
                        return prev.filter((id) => id !== triggeredRegionId);
                    }

                    return [...prev, triggeredRegionId];
                }
            };

            setSelectedRegionIds((prev) => {
                const triggeredRegion = props.allAvailableRegions.find((item) => item.id === triggeredRegionId);

                // region not found, return prev
                if (!triggeredRegion) {
                    return prev;
                }

                const newSelection = handleRegionSelection(prev, triggeredRegion);

                return newSelection;
            });
        },
        [props.allAvailableRegions, props.regions.districts, props.regions.main?.id]
    );

    const syncSelectedRegionsWithRemoteSelection = (remoteSelectedRegions: {type: RegionType; id: number}[]) => {
        if (!props.allAvailableRegions || props.allAvailableRegions.length === 0) {
            return;
        }

        // sync selection between every map with district selections e.g. header and sticky header
        const remoteSelectedRegionsIds = remoteSelectedRegions.map((item) => item.id);

        if (interactiveMapRegionSelection && isEqual(remoteSelectedRegionsIds, interactiveMapOriginRegions)) {
            setSelectedRegionIds(interactiveMapRegionSelection);
            return;
        }
        dispatch(searchFormActions.updateInteractiveMapOriginRegions(remoteSelectedRegionsIds));

        // one city selected - set only that city
        if (remoteSelectedRegions?.length === 1 && remoteSelectedRegions[0].type === RegionType.CITY) {
            setSelectedRegionIds([remoteSelectedRegions[0].id]);
            return;
        }

        // set all available regions
        if (
            remoteSelectedRegions?.length === 1 &&
            remoteSelectedRegions[0].type === RegionType.AGGREGATION &&
            aggregationToCityMap.has(remoteSelectedRegions[0].id)
        ) {
            setSelectedRegionIds(() => props.allAvailableRegions.map((item) => item.id));
            return;
        }

        // otherwise set selected regions if are available and map city to available districts
        setSelectedRegionIds(() => [
            ...(remoteSelectedRegions
                ?.filter((selectedRegion) => {
                    return props.allAvailableRegions.find((availableRegion) => availableRegion.id === selectedRegion.id);
                })
                .map((item) => item.id) || []),
            // also if whole city is selected and available add itd
            ...(props.regions.main?.id &&
            (remoteSelectedRegions.some((region) => region.id === props.regions.main?.id) ||
                props.regions.districts.every((region) => remoteSelectedRegions.some((remoteSelectedRegion) => remoteSelectedRegion.id === region.id)))
                ? [props.regions.main.id]
                : [])
        ]);
    };

    const toggleSelectedDistrict = () => {
        setSelectedRegionIds((prev) => {
            const allDistrictsSelected = checkAllDistrictBeenSelection(prev);

            if (allDistrictsSelected) {
                // remove city
                const regionIdsWithoutMain = prev.filter((id) => id !== props.regions.main?.id);

                return [
                    // remove districts
                    ...regionIdsWithoutMain.filter((id) => props.regions.districts.some((district) => district.id !== id))
                ];
            } else {
                return [
                    // remove districts
                    ...prev.filter((id) => !props.regions.districts.some((district) => district.id === id)),
                    // add city
                    props.regions.main?.id || -1
                ];
            }
        });
    };

    return {
        checkAllDistrictBeenSelection,
        hasAllDistrictBeenSelected,
        selectedRegionIdsState,
        selectedRegionIds,
        handleSelectedRegionsChange,
        toggleSelectedDistrict,
        syncSelectedRegionsWithRemoteSelection
    };
};
